import React, {
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { Box } from "rebass/styled-components";
import videojs from "video.js";
import "video.js/dist/video-js.css";

import { useFullscreen } from "MVVM/Hooks/useFullscreen";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  StyledPreviewPlayer,
  SourceLoadingOverlay,
  SourceLoadingText,
  PreloadVideo,
} from "./styledComponents";

interface PreviewPlayerProps {
  mp4Source: string | null;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  wrapperId: string;
}

export interface PreviewPlayerHandle {
  enterFullscreenAndPlay: () => Promise<void>;
  enterFullscreenOnly: () => Promise<void>;
}

export const PreviewPlayer = forwardRef<
  PreviewPlayerHandle,
  PreviewPlayerProps
>(({ mp4Source, onFullscreenChange, wrapperId }, ref) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<videojs.Player | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);

  const { isFullscreen, toggleFullscreen, enterFullscreen } = useFullscreen(
    wrapperRef,
    {
      onEnter: () => onFullscreenChange?.(true),
      onExit: () => onFullscreenChange?.(false),
    },
  );

  useImperativeHandle(
    ref,
    () => ({
      enterFullscreenAndPlay: async () => {
        const wrapper = document.getElementById(wrapperId);
        if (wrapper) wrapperRef.current = wrapper;

        try {
          await enterFullscreen();
        } catch (error) {
          console.error(error);
        }

        const player = playerRef.current;
        if (player && player.paused()) {
          try {
            await player.play();
          } catch (error) {
            console.error(error);
          }
        }
      },
      enterFullscreenOnly: async () => {
        const wrapper = document.getElementById(wrapperId);
        if (wrapper) wrapperRef.current = wrapper;

        try {
          await enterFullscreen();
        } catch (error) {
          console.error(error);
        }
      },
    }),
    [wrapperId, enterFullscreen],
  );

  const interceptFullscreenClick = useCallback(() => {
    const playerEl = playerRef.current?.el?.();
    if (!playerEl || !wrapperId) return;

    const originalButton = playerEl.querySelector(".vjs-fullscreen-control");
    if (!originalButton) return;

    const clonedFullscreen = originalButton.cloneNode(true);
    clonedFullscreen.addEventListener("click", async () => {
      const wrapper = document.getElementById(wrapperId);
      if (!wrapper) return;

      wrapperRef.current = wrapper;

      try {
        await toggleFullscreen();
      } catch (error) {
        console.error(error);
      }
    });

    originalButton.replaceWith(clonedFullscreen);
  }, [wrapperId, toggleFullscreen]);

  // Initialize video.js player
  useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      const player = videojs(videoElement, {
        controls: true,
        fluid: true,
        loop: true,
        sources: [],
        playsinline: true,
      });

      playerRef.current = player;

      player.one("ready", () => {
        try {
          interceptFullscreenClick();
        } catch (error) {
          console.error(error);
        }

        // Intercept clicks to handle double-click fullscreen toggle
        // without disrupting single-click play/pause
        const videoEl = player.el?.()?.querySelector("video");
        if (videoEl) {
          let clickTimer: ReturnType<typeof setTimeout> | null = null;
          let clickCount = 0;

          videoEl.addEventListener(
            "click",
            (e: Event) => {
              // Block video.js from handling ANY click — we manage play/pause ourselves
              e.stopImmediatePropagation();
              e.preventDefault();

              clickCount++;

              if (clickCount === 1) {
                // First click — wait to see if a second follows
                clickTimer = setTimeout(() => {
                  // Confirmed single click — toggle play/pause
                  clickCount = 0;
                  clickTimer = null;
                  if (player.paused()) {
                    player.play()?.catch(() => {});
                  } else {
                    player.pause();
                  }
                }, 300);
              } else if (clickCount === 2) {
                // Double click — toggle fullscreen, no play/pause toggle
                clearTimeout(clickTimer!);
                clickTimer = null;
                clickCount = 0;

                const wrapper = document.getElementById(wrapperId);
                if (!wrapper) return;
                wrapperRef.current = wrapper;

                toggleFullscreen().catch(console.error);
              }
            },
            true,
          );

          // Also block the native dblclick to prevent video.js fullscreen
          videoEl.addEventListener(
            "dblclick",
            (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            },
            true,
          );
        }
      });
    }
  }, [interceptFullscreenClick, wrapperId, toggleFullscreen]);

  // --- Source switching: preload crossfade with pause ---
  const [isSourceLoading, setIsSourceLoading] = useState(false);
  const [preloadSrc, setPreloadSrc] = useState<string | null>(null);
  const [showPreload, setShowPreload] = useState(false);
  const preloadVideoRef = useRef<HTMLVideoElement>(null);
  const wasPlayingRef = useRef<boolean>(false);
  const hadStartedRef = useRef<boolean>(false);
  const crossfadeInProgressRef = useRef(false);

  // Detect source change → pause player, start preloading new source
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    if (mp4Source) {
      const currentSource = player.currentSrc();
      if (currentSource !== mp4Source && !crossfadeInProgressRef.current) {
        // Save current state
        wasPlayingRef.current = !player.paused();
        hadStartedRef.current = player.hasStarted();

        // Pause immediately so the current frame stays visible
        player.pause();

        // Show loading overlay and start preloading new source
        crossfadeInProgressRef.current = true;
        setIsSourceLoading(true);
        setPreloadSrc(mp4Source);
      }
    } else {
      player.pause();
    }
  }, [mp4Source]);

  // Handle preload video: once it can play, swap it into the main player
  const handlePreloadReady = useCallback(() => {
    const player = playerRef.current;
    const preloadVideo = preloadVideoRef.current;
    if (!player || !preloadVideo || !preloadSrc) return;

    // Pause the preload and snapshot its time
    preloadVideo.pause();
    const snapshotTime = preloadVideo.currentTime;

    // Show the preload overlay on top of the main player
    setShowPreload(true);

    // Wait one frame for the preload overlay to render
    requestAnimationFrame(() => {
      if (hadStartedRef.current) {
        player.hasStarted(true);
      }

      const onLoaded = () => {
        player.off("loadeddata", onLoaded);

        // Seek main player to the preload's snapshot time
        const duration = player.duration() || 0;
        const seekTo = Math.min(snapshotTime, Math.max(0, duration));
        player.currentTime(seekTo);

        if (hadStartedRef.current) {
          player.hasStarted(true);
        }

        const onSeeked = () => {
          player.off("seeked", onSeeked);

          const finalize = () => {
            // Wait for the main player to render its frame
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Hide the preload overlay, clear loading state
                setShowPreload(false);
                setPreloadSrc(null);
                setIsSourceLoading(false);
                crossfadeInProgressRef.current = false;

                // Resume playback if it was playing before
                if (wasPlayingRef.current) {
                  player.play()?.catch(() => {});
                }
              });
            });
          };

          finalize();
        };

        player.one("seeked", onSeeked);
      };

      player.one("loadeddata", onLoaded);
      player.src({ src: preloadSrc, type: "video/mp4" });
      player.load();
    });
  }, [preloadSrc]);

  // Set up the preload video element when preloadSrc changes
  useEffect(() => {
    const player = playerRef.current;
    const preloadVideo = preloadVideoRef.current;
    if (!preloadSrc || !player || !preloadVideo) return;

    // Seek the preload to the main player's current time once it can play
    const onCanPlay = () => {
      preloadVideo.removeEventListener("canplay", onCanPlay);
      const targetTime = player.currentTime() || 0;
      preloadVideo.currentTime = targetTime;

      const onSeeked = () => {
        preloadVideo.removeEventListener("seeked", onSeeked);
        // Wait one frame to ensure decoder has rendered
        requestAnimationFrame(() => {
          handlePreloadReady();
        });
      };

      preloadVideo.addEventListener("seeked", onSeeked);
    };

    if (preloadVideo.readyState >= 3) {
      onCanPlay();
    } else {
      preloadVideo.addEventListener("canplay", onCanPlay);
    }

    preloadVideo.src = preloadSrc;
    preloadVideo.load();

    return () => {
      preloadVideo.removeEventListener("canplay", onCanPlay);
    };
  }, [preloadSrc, handlePreloadReady]);

  // Cleanup
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <StyledPreviewPlayer data-fullscreen={isFullscreen}>
      <Box data-vjs-player>
        <Box ref={videoRef} />
      </Box>
      {preloadSrc && (
        <PreloadVideo
          ref={preloadVideoRef}
          $visible={showPreload}
          muted
          playsInline
        />
      )}
      {isSourceLoading && (
        <SourceLoadingOverlay $isFullscreen={isFullscreen}>
          <LoadingSpinner width={40} height={40} />
          <SourceLoadingText>Loading updated preview</SourceLoadingText>
        </SourceLoadingOverlay>
      )}
    </StyledPreviewPlayer>
  );
});
